import React from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { PeriodPreset } from "../../hooks/usePeriodReportBuilder";

interface PeriodSelectorProps {
  selectedPeriod: PeriodPreset;
  onPeriodChange: (period: PeriodPreset) => void;
  customStartDate: Date | null;
  customEndDate: Date | null;
  onCustomStartDateChange: (date: Date | null) => void;
  onCustomEndDateChange: (date: Date | null) => void;
  onGenerateReport: () => void;
  canGenerate: boolean;
  loading: boolean;
}

const getPeriodButtonClass = (
  preset: PeriodPreset,
  selectedPeriod: PeriodPreset,
): string =>
  `px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
    selectedPeriod === preset
      ? "bg-white dark:bg-slate-600 shadow text-black dark:text-white hover:bg-gray-50 dark:hover:bg-slate-500"
      : "text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700"
  }`;

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedPeriod,
  onPeriodChange,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
  onGenerateReport,
  canGenerate,
  loading,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 bg-gray-100 dark:bg-slate-800 p-2 rounded-lg">
        <Button
          className={getPeriodButtonClass("last_30_days", selectedPeriod)}
          onClick={() => onPeriodChange("last_30_days")}
          type="button"
          variant="ghost"
        >
          Last 30 Days
        </Button>

        <Button
          className={getPeriodButtonClass("current_month", selectedPeriod)}
          onClick={() => onPeriodChange("current_month")}
          type="button"
          variant="ghost"
        >
          Current Month
        </Button>

        <Button
          className={getPeriodButtonClass("last_month", selectedPeriod)}
          onClick={() => onPeriodChange("last_month")}
          type="button"
          variant="ghost"
        >
          Last Month
        </Button>

        <Button
          className={getPeriodButtonClass("current_year", selectedPeriod)}
          onClick={() => onPeriodChange("current_year")}
          type="button"
          variant="ghost"
        >
          Current Year
        </Button>

        <Button
          className={getPeriodButtonClass("last_year", selectedPeriod)}
          onClick={() => onPeriodChange("last_year")}
          type="button"
          variant="ghost"
        >
          Last Year
        </Button>

        <Button
          className={getPeriodButtonClass("custom", selectedPeriod)}
          onClick={() => onPeriodChange("custom")}
          type="button"
          variant="ghost"
        >
          Custom
        </Button>

        <Button
          className="ml-auto"
          disabled={!canGenerate || loading}
          onClick={onGenerateReport}
          type="button"
        >
          {loading ? "Generating..." : "Generate Report"}
        </Button>
      </div>

      {selectedPeriod === "custom" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Date
            </label>
            <DatePicker
              onChange={onCustomStartDateChange}
              placeholder="Select start date"
              value={customStartDate}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Date
            </label>
            <DatePicker
              minDate={customStartDate}
              onChange={onCustomEndDateChange}
              placeholder="Select end date"
              value={customEndDate}
            />
          </div>
        </div>
      )}
    </div>
  );
};
